import { useEffect, type JSX } from 'react';
import { useBlocker } from 'react-router-dom';

import { ConfirmDialog } from './ConfirmDialog';

interface LeaveGuardProps {
  /** Mientras sea `true`, salir de la página pide confirmación. */
  when: boolean;
  title: string;
  message: string;
  stayLabel?: string;
  leaveLabel?: string;
}

/**
 * OS-1713 — aviso al salir con algo que se perdería: navegación interna (`useBlocker`, requiere el
 * data router de `main.tsx`) y cierre o recarga de la pestaña (`beforeunload`, con el texto
 * genérico del navegador).
 */
export function LeaveGuard({
  when,
  title,
  message,
  stayLabel = 'Quedarme',
  leaveLabel = 'Salir sin guardar',
}: LeaveGuardProps): JSX.Element | null {
  // OS-1718 — solo bloquea si cambia la ruta: cambiar la query de la misma página (p. ej. la
  // pestaña `?tab=` de la ficha) no desmonta nada ni pierde lo editado.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!when) {
      return undefined;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      // Requerido por algunos navegadores para mostrar el aviso.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return (): void => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [when]);

  if (blocker.state !== 'blocked') {
    return null;
  }
  return (
    <ConfirmDialog
      title={title}
      confirmLabel={leaveLabel}
      cancelLabel={stayLabel}
      danger
      onConfirm={() => blocker.proceed()}
      onCancel={() => blocker.reset()}
    >
      <p>{message}</p>
    </ConfirmDialog>
  );
}
